import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Globe,
  Palette,
  Phone,
  MapPin,
  Clock,
  Share2,
  Search,
  Settings,
  Car,
  Loader2,
  Download,
  Save,
  RefreshCw,
  Image,
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  Eye,
  EyeOff,
  X,
  Upload,
  FileText,
} from 'lucide-react';
import { revendaMaisApi, siteUploadApi } from '../../services/integracoesApi';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { api } from '../../services/api';
import { Panel, Badge, Button, buttonClass } from '../../design-system';
import type { SiteConfiguracoesDto, SiteConfiguracoesUpdateDto } from '../../types/integracoes';

const inputClass =
  'w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none';
const textareaClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

// Extensões de imagem permitidas
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Tipo do Banner
interface SiteBanner {
  id: number;
  titulo?: string;
  subtitulo?: string;
  textoBotao?: string;
  linkBotao?: string;
  imagemDesktopUrl?: string;
  imagemMobileUrl?: string;
  posicaoTexto: string;
  corTexto: string;
  corOverlay: string;
  ordem: number;
  ativo: boolean;
  dataInicio?: string;
  dataFim?: string;
}

type TabType = 'basico' | 'banners' | 'cores' | 'contato' | 'endereco' | 'horarios' | 'redes' | 'seo' | 'funcionalidades' | 'veiculos' | 'conteudo';

const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
  { id: 'basico', label: 'Básico', icon: Globe },
  { id: 'banners', label: 'Banners', icon: Image },
  { id: 'conteudo', label: 'Conteúdo', icon: FileText },
  { id: 'cores', label: 'Cores', icon: Palette },
  { id: 'contato', label: 'Contato', icon: Phone },
  { id: 'endereco', label: 'Endereço', icon: MapPin },
  { id: 'horarios', label: 'Horários', icon: Clock },
  { id: 'redes', label: 'Redes Sociais', icon: Share2 },
  { id: 'seo', label: 'SEO', icon: Search },
  { id: 'funcionalidades', label: 'Funcionalidades', icon: Settings },
  { id: 'veiculos', label: 'Veículos', icon: Car },
];

export default function SiteConfig() {
  const { showSuccess, showApiError, showError } = useNotification();
  const [config, setConfig] = useState<SiteConfiguracoesDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('basico');

  // Logo state
  const [logoUrl, setLogoUrl] = useState('');
  const [savingLogo, setSavingLogo] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Banners state
  const [banners, setBanners] = useState<SiteBanner[]>([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState<SiteBanner | null>(null);
  const [savingBanner, setSavingBanner] = useState(false);
  const [uploadingBannerDesktop, setUploadingBannerDesktop] = useState(false);
  const [uploadingBannerMobile, setUploadingBannerMobile] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    titulo: '',
    subtitulo: '',
    textoBotao: 'Ver Estoque',
    linkBotao: '/veiculos',
    imagemDesktopUrl: '',
    imagemMobileUrl: '',
    posicaoTexto: 'center',
    corTexto: '#FFFFFF',
    corOverlay: 'rgba(0,0,0,0.5)',
    ativo: true,
    dataInicio: '',
    dataFim: '',
  });

  const { register, handleSubmit, reset, watch, setValue } = useForm<SiteConfiguracoesUpdateDto>();

  // Watch color values for syncing color picker and text input
  const watchedColors = watch([
    'corPrimaria', 'corSecundaria', 'corFundo', 'corTexto',
    'corBotao', 'corBotaoTexto', 'corHeader', 'corFooter'
  ]);

  // Load banners
  const loadBanners = useCallback(async () => {
    try {
      setLoadingBanners(true);
      const response = await api.get<SiteBanner[]>('/site/banners');
      setBanners(response.data);
    } catch (error) {
      console.error('Erro ao carregar banners:', error);
    } finally {
      setLoadingBanners(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    loadBanners();
    return () => controller.abort();
  }, [loadBanners]);

  const loadData = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const data = await revendaMaisApi.getSiteConfig(signal);
      setConfig(data);
      setLogoUrl(data.logoUrl || '');
      reset(data);
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        showApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Salvar logo
  const handleSaveLogo = async () => {
    try {
      setSavingLogo(true);
      await revendaMaisApi.updateSiteConfig({ logoUrl: logoUrl || undefined });
      showSuccess('Logo atualizada com sucesso!');
      await loadData();
    } catch (error) {
      showApiError(error);
    } finally {
      setSavingLogo(false);
    }
  };

  // Upload de logo
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar extensão
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
      showError(`Extensão não permitida. Use: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validar tamanho
    if (file.size > MAX_FILE_SIZE) {
      showError('Arquivo muito grande. Máximo permitido: 10MB');
      return;
    }

    try {
      setUploadingLogo(true);
      const result = await siteUploadApi.uploadLogo(file);
      if (result.sucesso) {
        setLogoUrl(result.url);
        // Salvar automaticamente
        await revendaMaisApi.updateSiteConfig({ logoUrl: result.url });
        showSuccess('Logo enviada e salva com sucesso!');
        await loadData();
      }
    } catch (error) {
      showApiError(error);
    } finally {
      setUploadingLogo(false);
      // Limpar input
      e.target.value = '';
    }
  };

  // Upload de imagem de banner
  const handleBannerImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'desktop' | 'mobile'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar extensão
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
      showError(`Extensão não permitida. Use: ${ALLOWED_IMAGE_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validar tamanho
    if (file.size > MAX_FILE_SIZE) {
      showError('Arquivo muito grande. Máximo permitido: 10MB');
      return;
    }

    try {
      if (type === 'desktop') {
        setUploadingBannerDesktop(true);
      } else {
        setUploadingBannerMobile(true);
      }

      const result = await siteUploadApi.uploadBanner(file);
      if (result.sucesso) {
        if (type === 'desktop') {
          setBannerForm({ ...bannerForm, imagemDesktopUrl: result.url });
        } else {
          setBannerForm({ ...bannerForm, imagemMobileUrl: result.url });
        }
        showSuccess('Imagem enviada com sucesso!');
      }
    } catch (error) {
      showApiError(error);
    } finally {
      if (type === 'desktop') {
        setUploadingBannerDesktop(false);
      } else {
        setUploadingBannerMobile(false);
      }
      // Limpar input
      e.target.value = '';
    }
  };

  // Banner CRUD functions
  const openCreateBanner = () => {
    setEditingBanner(null);
    setBannerForm({
      titulo: '',
      subtitulo: '',
      textoBotao: 'Ver Estoque',
      linkBotao: '/veiculos',
      imagemDesktopUrl: '',
      imagemMobileUrl: '',
      posicaoTexto: 'center',
      corTexto: '#FFFFFF',
      corOverlay: 'rgba(0,0,0,0.5)',
      ativo: true,
      dataInicio: '',
      dataFim: '',
    });
    setShowBannerModal(true);
  };

  const openEditBanner = (banner: SiteBanner) => {
    setEditingBanner(banner);
    setBannerForm({
      titulo: banner.titulo || '',
      subtitulo: banner.subtitulo || '',
      textoBotao: banner.textoBotao || 'Ver Estoque',
      linkBotao: banner.linkBotao || '/veiculos',
      imagemDesktopUrl: banner.imagemDesktopUrl || '',
      imagemMobileUrl: banner.imagemMobileUrl || '',
      posicaoTexto: banner.posicaoTexto || 'center',
      corTexto: banner.corTexto || '#FFFFFF',
      corOverlay: banner.corOverlay || 'rgba(0,0,0,0.5)',
      ativo: banner.ativo,
      dataInicio: banner.dataInicio ? banner.dataInicio.split('T')[0] : '',
      dataFim: banner.dataFim ? banner.dataFim.split('T')[0] : '',
    });
    setShowBannerModal(true);
  };

  const handleSaveBanner = async () => {
    if (!bannerForm.imagemDesktopUrl) {
      showError('A imagem do banner é obrigatória');
      return;
    }

    try {
      setSavingBanner(true);
      const payload = {
        ...bannerForm,
        dataInicio: bannerForm.dataInicio || null,
        dataFim: bannerForm.dataFim || null,
      };

      if (editingBanner) {
        await api.put(`/site/banners/${editingBanner.id}`, payload);
        showSuccess('Banner atualizado com sucesso!');
      } else {
        await api.post('/site/banners', payload);
        showSuccess('Banner criado com sucesso!');
      }

      setShowBannerModal(false);
      await loadBanners();
    } catch (error) {
      showApiError(error);
    } finally {
      setSavingBanner(false);
    }
  };

  const handleToggleBannerAtivo = async (banner: SiteBanner) => {
    try {
      await api.patch(`/site/banners/${banner.id}/ativo`, { ativo: !banner.ativo });
      showSuccess(banner.ativo ? 'Banner desativado' : 'Banner ativado');
      await loadBanners();
    } catch (error) {
      showApiError(error);
    }
  };

  const handleDeleteBanner = async (banner: SiteBanner) => {
    if (!confirm(`Excluir o banner "${banner.titulo || 'Sem título'}"?`)) return;

    try {
      await api.delete(`/site/banners/${banner.id}`);
      showSuccess('Banner excluído com sucesso!');
      await loadBanners();
    } catch (error) {
      showApiError(error);
    }
  };

  const handleReorderBanners = async (dragIndex: number, dropIndex: number) => {
    if (dragIndex === dropIndex) return;

    const newBanners = [...banners];
    const [removed] = newBanners.splice(dragIndex, 1);
    newBanners.splice(dropIndex, 0, removed);
    setBanners(newBanners);

    try {
      await api.post('/site/banners/reordenar', newBanners.map(b => b.id));
    } catch (error) {
      showApiError(error);
      await loadBanners();
    }
  };

  const onSubmit = async (data: SiteConfiguracoesUpdateDto) => {
    try {
      setSaving(true);
      await revendaMaisApi.updateSiteConfig(data);
      showSuccess('Configurações salvas com sucesso!');
      await loadData();
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleImportarDoRevendaMais = async () => {
    try {
      setImporting(true);
      const result = await revendaMaisApi.importarSiteConfig();
      if (result.sucesso) {
        showSuccess(`${result.mensagem} (${result.camposImportados.length} campos importados)`);
        await loadData();
      } else {
        showError(result.mensagem);
      }
    } catch (error) {
      showApiError(error);
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Configurações do Site"
        subtitle="Configure as informações que serão exibidas no seu site próprio"
      />

      {/* Status Card */}
      <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100">
              <Globe className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold tracking-tight text-slate-900">
                {config?.nomeSite || 'Site não configurado'}
              </h3>
              {config?.dominio && (
                <a
                  href={`https://${config.dominio}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary-600 hover:underline"
                >
                  {config.dominio}
                </a>
              )}
              {config?.dataImportacaoRevendaMais && (
                <p className="text-xs text-slate-400 mt-1">
                  Última importação do RevendaMais: {formatDate(config.dataImportacaoRevendaMais)}
                </p>
              )}
            </div>
          </div>

          <Button
            variant="primary"
            onClick={handleImportarDoRevendaMais}
            disabled={importing}
          >
            {importing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Importar do RevendaMais
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)]">
        <div className="border-b border-slate-100 overflow-x-auto">
          <nav className="flex min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          {/* Tab: Básico */}
          {activeTab === 'basico' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Informações Básicas</h3>

              {/* Logo */}
              <Panel title="Logomarca do Site" icon={<Upload className="h-4 w-4" />}>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 space-y-3">
                    {/* Upload de arquivo */}
                    <div>
                      <label className="block text-sm text-slate-500 mb-1">
                        Enviar Logo (PNG, SVG ou JPG recomendado)
                      </label>
                      <div className="flex items-center gap-2">
                        <label className={`${buttonClass('secondary')} cursor-pointer`}>
                          <Upload className="w-4 h-4 text-slate-500" />
                          <span>
                            {uploadingLogo ? 'Enviando...' : 'Escolher arquivo'}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            disabled={uploadingLogo}
                            className="hidden"
                          />
                        </label>
                        {uploadingLogo && <Loader2 className="w-4 h-4 animate-spin text-primary-600" />}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Máximo 10MB. A imagem será salva automaticamente.
                      </p>
                    </div>

                    {/* URL manual (alternativa) */}
                    <div>
                      <label className="block text-sm text-slate-500 mb-1">
                        Ou cole a URL da imagem
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={logoUrl}
                          onChange={(e) => setLogoUrl(e.target.value)}
                          className={inputClass}
                          placeholder="https://exemplo.com/sua-logo.png"
                        />
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={handleSaveLogo}
                          disabled={savingLogo || !logoUrl}
                        >
                          {savingLogo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          Salvar
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-xs text-slate-400">Preview</span>
                    {logoUrl ? (
                      <div className="w-40 h-20 border border-slate-200 rounded-xl bg-white flex items-center justify-center p-2">
                        <img
                          src={logoUrl}
                          alt="Preview da logo"
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.src = '';
                            e.currentTarget.alt = 'Erro ao carregar';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-40 h-20 border-2 border-dashed border-slate-200 rounded-xl bg-white flex items-center justify-center text-slate-400 text-xs">
                        Sem logo
                      </div>
                    )}
                    {logoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setLogoUrl('');
                          handleSaveLogo();
                        }}
                        className="text-xs font-medium text-rose-600 hover:underline"
                      >
                        Remover logo
                      </button>
                    )}
                  </div>
                </div>
              </Panel>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Nome do Site
                  </label>
                  <input
                    type="text"
                    {...register('nomeSite')}
                    className={inputClass}
                    placeholder="Ex: AutoVeículos Premium"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Subtítulo / Slogan
                  </label>
                  <input
                    type="text"
                    {...register('subtitulo')}
                    className={inputClass}
                    placeholder="Ex: O melhor em seminovos"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Domínio
                  </label>
                  <input
                    type="text"
                    {...register('dominio')}
                    className={inputClass}
                    placeholder="Ex: www.suarevenda.com.br"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register('siteAtivo')}
                      className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700">Site ativo</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Banners */}
          {activeTab === 'banners' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold tracking-tight text-slate-900">Banners do Site</h3>
                  <p className="text-sm text-slate-500">Gerencie os banners que aparecem na página inicial do seu site</p>
                </div>
                <Button type="button" variant="primary" onClick={openCreateBanner}>
                  <Plus className="w-4 h-4" />
                  Novo Banner
                </Button>
              </div>

              {loadingBanners ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                </div>
              ) : banners.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
                  <Image className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Nenhum banner cadastrado</p>
                  <p className="text-sm text-slate-400 mb-4">Adicione banners para exibir na home do site</p>
                  <button
                    type="button"
                    onClick={openCreateBanner}
                    className={buttonClass('primary')}
                  >
                    <Plus className="w-4 h-4" />
                    Criar Primeiro Banner
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {banners.map((banner, index) => (
                    <div
                      key={banner.id}
                      className={`border border-slate-200 rounded-xl overflow-hidden ${!banner.ativo ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-stretch">
                        {/* Drag Handle */}
                        <div
                          className="w-10 bg-slate-50 flex items-center justify-center cursor-move border-r border-slate-200"
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData('text/plain', index.toString())}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                            handleReorderBanners(dragIndex, index);
                          }}
                        >
                          <GripVertical className="w-4 h-4 text-slate-400" />
                        </div>

                        {/* Thumbnail */}
                        <div className="w-40 h-24 bg-slate-100 flex-shrink-0">
                          {banner.imagemDesktopUrl ? (
                            <img
                              src={banner.imagemDesktopUrl}
                              alt={banner.titulo || 'Banner'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <Image className="w-8 h-8" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-slate-900">
                                {banner.titulo || 'Sem título'}
                              </h4>
                              <p className="text-sm text-slate-500 mt-0.5">
                                {banner.subtitulo || 'Sem subtítulo'}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs">
                                <Badge tone={banner.ativo ? 'success' : 'neutral'}>
                                  {banner.ativo ? 'Ativo' : 'Inativo'}
                                </Badge>
                                <span className="text-slate-400">Ordem: {banner.ordem}</span>
                                {banner.textoBotao && (
                                  <span className="text-slate-400">Botão: {banner.textoBotao}</span>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleToggleBannerAtivo(banner)}
                                className={`p-2 rounded-lg transition-colors ${banner.ativo ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                                title={banner.ativo ? 'Desativar' : 'Ativar'}
                              >
                                {banner.ativo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => openEditBanner(banner)}
                                className="p-2 text-slate-500 hover:text-primary-600 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteBanner(banner)}
                                className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <h4 className="font-semibold text-sky-900 mb-2">Dicas para banners</h4>
                <ul className="text-sm text-sky-700 space-y-1">
                  <li>• Tamanho recomendado: 1920x700 pixels (desktop) e 800x600 (mobile)</li>
                  <li>• Use imagens de alta qualidade com bom contraste para o texto</li>
                  <li>• Os banners serão exibidos em carrossel automático no site</li>
                  <li>• Arraste os banners para alterar a ordem de exibição</li>
                </ul>
              </div>
            </div>
          )}

          {/* Tab: Cores */}
          {activeTab === 'cores' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Cores do Tema</h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'corPrimaria', label: 'Cor Primária', index: 0 },
                  { name: 'corSecundaria', label: 'Cor Secundária', index: 1 },
                  { name: 'corFundo', label: 'Cor de Fundo', index: 2 },
                  { name: 'corTexto', label: 'Cor do Texto', index: 3 },
                  { name: 'corBotao', label: 'Cor do Botão', index: 4 },
                  { name: 'corBotaoTexto', label: 'Cor Texto Botão', index: 5 },
                  { name: 'corHeader', label: 'Cor do Header', index: 6 },
                  { name: 'corFooter', label: 'Cor do Footer', index: 7 },
                ].map((field) => (
                  <div key={field.name}>
                    <label className={labelClass}>
                      {field.label}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={watchedColors[field.index] || '#000000'}
                        onChange={(e) => setValue(field.name as keyof SiteConfiguracoesUpdateDto, e.target.value)}
                        className="h-10 w-14 rounded-xl border border-slate-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        {...register(field.name as keyof SiteConfiguracoesUpdateDto)}
                        className={inputClass}
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab: Contato */}
          {activeTab === 'contato' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Informações de Contato</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Telefone Fixo
                  </label>
                  <input
                    type="tel"
                    {...register('telefoneFixo')}
                    className={inputClass}
                    placeholder="(00) 0000-0000"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Telefone Celular
                  </label>
                  <input
                    type="tel"
                    {...register('telefoneCelular')}
                    className={inputClass}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    {...register('whatsappNumero')}
                    className={inputClass}
                    placeholder="5500000000000"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register('whatsappAtivo')}
                      className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700">WhatsApp ativo no site</span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Mensagem padrão do WhatsApp
                  </label>
                  <textarea
                    {...register('whatsappMensagem')}
                    className={textareaClass}
                    rows={3}
                    placeholder="Olá! Vim pelo site e gostaria de mais informações..."
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    E-mail Principal
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className={inputClass}
                    placeholder="contato@suarevenda.com.br"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    E-mail Secundário
                  </label>
                  <input
                    type="email"
                    {...register('emailSecundario')}
                    className={inputClass}
                    placeholder="vendas@suarevenda.com.br"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Endereço */}
          {activeTab === 'endereco' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Endereço</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Logradouro
                  </label>
                  <input
                    type="text"
                    {...register('endereco')}
                    className={inputClass}
                    placeholder="Av. Brasil"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Número
                  </label>
                  <input
                    type="text"
                    {...register('enderecoNumero')}
                    className={inputClass}
                    placeholder="1234"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Complemento
                  </label>
                  <input
                    type="text"
                    {...register('enderecoComplemento')}
                    className={inputClass}
                    placeholder="Sala 01"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Bairro
                  </label>
                  <input
                    type="text"
                    {...register('enderecoBairro')}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    CEP
                  </label>
                  <input
                    type="text"
                    {...register('enderecoCep')}
                    className={inputClass}
                    placeholder="00000-000"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Cidade
                  </label>
                  <input
                    type="text"
                    {...register('enderecoCidade')}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    UF
                  </label>
                  <input
                    type="text"
                    {...register('enderecoUf')}
                    maxLength={2}
                    className={inputClass}
                    placeholder="SP"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className={labelClass}>
                    Link do Google Maps
                  </label>
                  <input
                    type="url"
                    {...register('googleMapsLink')}
                    className={inputClass}
                    placeholder="https://maps.google.com/..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Horários */}
          {activeTab === 'horarios' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Horário de Funcionamento</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Segunda a Sexta
                  </label>
                  <input
                    type="text"
                    {...register('horarioSegundaSexta')}
                    className={inputClass}
                    placeholder="08:00 às 18:00"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Sábado
                  </label>
                  <input
                    type="text"
                    {...register('horarioSabado')}
                    className={inputClass}
                    placeholder="08:00 às 13:00"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Domingo
                  </label>
                  <input
                    type="text"
                    {...register('horarioDomingo')}
                    className={inputClass}
                    placeholder="Fechado"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Feriados
                  </label>
                  <input
                    type="text"
                    {...register('horarioFeriados')}
                    className={inputClass}
                    placeholder="Fechado"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Redes Sociais */}
          {activeTab === 'redes' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Redes Sociais</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: 'facebookUrl', label: 'Facebook', placeholder: 'https://facebook.com/suarevenda' },
                  { name: 'instagramUrl', label: 'Instagram', placeholder: 'https://instagram.com/suarevenda' },
                  { name: 'youtubeUrl', label: 'YouTube', placeholder: 'https://youtube.com/@suarevenda' },
                  { name: 'twitterUrl', label: 'Twitter / X', placeholder: 'https://twitter.com/suarevenda' },
                  { name: 'tiktokUrl', label: 'TikTok', placeholder: 'https://tiktok.com/@suarevenda' },
                  { name: 'linkedinUrl', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/suarevenda' },
                ].map((field) => (
                  <div key={field.name}>
                    <label className={labelClass}>
                      {field.label}
                    </label>
                    <input
                      type="url"
                      {...register(field.name as keyof SiteConfiguracoesUpdateDto)}
                      className={inputClass}
                      placeholder={field.placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab: SEO */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">SEO e Analytics</h3>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    Meta Título
                  </label>
                  <input
                    type="text"
                    {...register('metaTitulo')}
                    className={inputClass}
                    placeholder="Título que aparece na aba do navegador e resultados do Google"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Meta Descrição
                  </label>
                  <textarea
                    {...register('metaDescricao')}
                    className={textareaClass}
                    rows={3}
                    placeholder="Descrição que aparece nos resultados do Google (até 160 caracteres)"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Palavras-chave
                  </label>
                  <input
                    type="text"
                    {...register('metaPalavrasChave')}
                    className={inputClass}
                    placeholder="carros, seminovos, veículos, sua cidade"
                  />
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Rastreamento</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>
                        Google Analytics ID
                      </label>
                      <input
                        type="text"
                        {...register('googleAnalyticsId')}
                        className={inputClass}
                        placeholder="G-XXXXXXXXXX"
                      />
                    </div>

                    <div>
                      <label className={labelClass}>
                        Google Tag Manager ID
                      </label>
                      <input
                        type="text"
                        {...register('googleTagManagerId')}
                        className={inputClass}
                        placeholder="GTM-XXXXXXX"
                      />
                    </div>

                    <div>
                      <label className={labelClass}>
                        Facebook Pixel ID
                      </label>
                      <input
                        type="text"
                        {...register('facebookPixelId')}
                        className={inputClass}
                        placeholder="XXXXXXXXXXXXXXXX"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Funcionalidades */}
          {activeTab === 'funcionalidades' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Funcionalidades do Site</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: 'exibirFinanciamento', label: 'Exibir opção de Financiamento' },
                  { name: 'exibirConsorcio', label: 'Exibir opção de Consórcio' },
                  { name: 'exibirTroca', label: 'Exibir opção de Troca' },
                  { name: 'exibirComparativo', label: 'Exibir comparativo de veículos' },
                  { name: 'exibirBusca', label: 'Exibir campo de busca' },
                  { name: 'exibirFiltros', label: 'Exibir filtros de busca' },
                  { name: 'exibirPrecoPromocional', label: 'Exibir preço promocional' },
                  { name: 'exibirFormularioContato', label: 'Exibir formulário de contato' },
                  { name: 'exibirChatOnline', label: 'Exibir chat online' },
                ].map((field) => (
                  <label key={field.name} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register(field.name as keyof SiteConfiguracoesUpdateDto)}
                      className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Tab: Conteúdo */}
          {activeTab === 'conteudo' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Conteúdo do Site</h3>

              <div>
                <label className={labelClass}>
                  Texto da Página "Sobre a Empresa"
                </label>
                <p className="text-sm text-slate-500 mb-2">
                  Este texto será exibido na página "Empresa" do site. Você pode usar quebras de linha para separar parágrafos.
                </p>
                <textarea
                  {...register('textoSobre')}
                  rows={10}
                  className={textareaClass}
                  placeholder="Conte a história da sua empresa, seus valores, diferenciais e o que a torna especial..."
                />
              </div>

              <div>
                <label className={labelClass}>
                  Texto do Rodapé
                </label>
                <p className="text-sm text-slate-500 mb-2">
                  Texto adicional exibido no rodapé do site.
                </p>
                <textarea
                  {...register('textoRodape')}
                  rows={3}
                  className={textareaClass}
                  placeholder="Ex: © 2024 Sua Empresa. Todos os direitos reservados."
                />
              </div>
            </div>
          )}

          {/* Tab: Veículos */}
          {activeTab === 'veiculos' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Configurações de Veículos</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-slate-700">Informações a exibir</h4>
                  {[
                    { name: 'exibirPlaca', label: 'Exibir placa do veículo' },
                    { name: 'exibirKmCompleto', label: 'Exibir quilometragem completa' },
                    { name: 'exibirOpcionais', label: 'Exibir lista de opcionais' },
                    { name: 'exibirFicha', label: 'Exibir ficha técnica' },
                  ].map((field) => (
                    <label key={field.name} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        {...register(field.name as keyof SiteConfiguracoesUpdateDto)}
                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-slate-700">{field.label}</span>
                    </label>
                  ))}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>
                      Quantidade de veículos em destaque
                    </label>
                    <input
                      type="number"
                      {...register('quantidadeVeiculosDestaque')}
                      className={inputClass}
                      min="0"
                      max="20"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Ordem de exibição dos veículos
                    </label>
                    <select
                      {...register('ordemExibicaoVeiculos')}
                      className={inputClass}
                    >
                      <option value="RECENTES">Mais recentes primeiro</option>
                      <option value="PRECO_MENOR">Menor preço primeiro</option>
                      <option value="PRECO_MAIOR">Maior preço primeiro</option>
                      <option value="ALFABETICA">Ordem alfabética</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="border-t border-slate-100 mt-6 pt-6">
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => loadData()}>
                <RefreshCw className="h-4 w-4" />
                Recarregar
              </Button>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Salvar Configurações
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Modal de Banner */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Header com toggle de ativo */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-4">
                <h3 className="font-bold tracking-tight text-lg text-slate-900">
                  {editingBanner ? 'Editar Banner' : 'Novo Banner'}
                </h3>
                {/* Toggle de Ativo em destaque */}
                <label className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-colors ${
                  bannerForm.ativo
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  <input
                    type="checkbox"
                    checked={bannerForm.ativo}
                    onChange={(e) => setBannerForm({ ...bannerForm, ativo: e.target.checked })}
                    className="sr-only"
                  />
                  <div className={`w-8 h-4 rounded-full relative transition-colors ${
                    bannerForm.ativo ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}>
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                      bannerForm.ativo ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </div>
                  <span className="text-sm font-semibold">
                    {bannerForm.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </label>
              </div>
              <button type="button" onClick={() => setShowBannerModal(false)} className="p-1 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* Preview em tempo real */}
              <div className="relative h-48 rounded-xl overflow-hidden bg-slate-100">
                {bannerForm.imagemDesktopUrl ? (
                  <img
                    src={bannerForm.imagemDesktopUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                    <div className="text-center">
                      <Image className="w-12 h-12 mx-auto mb-2" />
                      <span className="text-sm">Adicione uma imagem</span>
                    </div>
                  </div>
                )}
                <div
                  className="absolute inset-0 flex items-center justify-center text-center p-4"
                  style={{ backgroundColor: bannerForm.corOverlay }}
                >
                  <div style={{ color: bannerForm.corTexto }}>
                    {bannerForm.titulo ? (
                      <h4 className="text-2xl font-bold">{bannerForm.titulo}</h4>
                    ) : (
                      <h4 className="text-2xl font-bold opacity-50 italic">Sem título</h4>
                    )}
                    {bannerForm.subtitulo ? (
                      <p className="text-sm opacity-80 mt-1">{bannerForm.subtitulo}</p>
                    ) : null}
                    {bannerForm.textoBotao ? (
                      <div className="mt-4 inline-block px-4 py-2 bg-white/90 text-black text-sm font-medium rounded">
                        {bannerForm.textoBotao}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Seção 1: Imagens */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2 [&>svg]:text-slate-400">
                  <Image className="w-4 h-4" />
                  Imagens do Banner
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  {/* Desktop */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Imagem Desktop <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {uploadingBannerDesktop ? 'Enviando...' : 'Clique para enviar'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleBannerImageUpload(e, 'desktop')}
                          disabled={uploadingBannerDesktop}
                          className="hidden"
                        />
                      </label>
                      {uploadingBannerDesktop && <Loader2 className="w-5 h-5 animate-spin text-primary-600" />}
                    </div>
                    <input
                      type="url"
                      value={bannerForm.imagemDesktopUrl}
                      onChange={(e) => setBannerForm({ ...bannerForm, imagemDesktopUrl: e.target.value })}
                      className={inputClass}
                      placeholder="Ou cole a URL da imagem..."
                    />
                    {bannerForm.imagemDesktopUrl && (
                      <div className="relative h-24 bg-slate-100 rounded-xl overflow-hidden">
                        <img src={bannerForm.imagemDesktopUrl} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setBannerForm({ ...bannerForm, imagemDesktopUrl: '' })}
                          className="absolute top-2 right-2 p-1.5 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-slate-400">Tamanho recomendado: 1920x700px</p>
                  </div>

                  {/* Mobile */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Imagem Mobile <span className="text-slate-400">(opcional)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {uploadingBannerMobile ? 'Enviando...' : 'Clique para enviar'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleBannerImageUpload(e, 'mobile')}
                          disabled={uploadingBannerMobile}
                          className="hidden"
                        />
                      </label>
                      {uploadingBannerMobile && <Loader2 className="w-5 h-5 animate-spin text-primary-600" />}
                    </div>
                    <input
                      type="url"
                      value={bannerForm.imagemMobileUrl}
                      onChange={(e) => setBannerForm({ ...bannerForm, imagemMobileUrl: e.target.value })}
                      className={inputClass}
                      placeholder="Ou cole a URL da imagem..."
                    />
                    {bannerForm.imagemMobileUrl && (
                      <div className="relative h-24 bg-slate-100 rounded-xl overflow-hidden">
                        <img src={bannerForm.imagemMobileUrl} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setBannerForm({ ...bannerForm, imagemMobileUrl: '' })}
                          className="absolute top-2 right-2 p-1.5 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-slate-400">Tamanho recomendado: 750x500px</p>
                  </div>
                </div>
              </div>

              {/* Seção 2: Conteúdo */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2 [&>svg]:text-slate-400">
                  <Pencil className="w-4 h-4" />
                  Conteúdo do Banner
                  <span className="text-xs text-slate-400 font-normal ml-2">
                    (deixe em branco para não exibir)
                  </span>
                </h4>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>
                        Título
                      </label>
                      <input
                        type="text"
                        value={bannerForm.titulo}
                        onChange={(e) => setBannerForm({ ...bannerForm, titulo: e.target.value })}
                        className={inputClass}
                        placeholder="Ex: Seu próximo carro está aqui"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Subtítulo
                      </label>
                      <input
                        type="text"
                        value={bannerForm.subtitulo}
                        onChange={(e) => setBannerForm({ ...bannerForm, subtitulo: e.target.value })}
                        className={inputClass}
                        placeholder="Ex: Veículos premium com procedência"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>
                        Texto do Botão
                        <span className="text-xs text-slate-400 font-normal ml-2">
                          (deixe vazio para ocultar)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={bannerForm.textoBotao}
                        onChange={(e) => setBannerForm({ ...bannerForm, textoBotao: e.target.value })}
                        className={inputClass}
                        placeholder="Ex: Ver Estoque"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Link do Botão
                      </label>
                      <input
                        type="text"
                        value={bannerForm.linkBotao}
                        onChange={(e) => setBannerForm({ ...bannerForm, linkBotao: e.target.value })}
                        className={inputClass}
                        placeholder="/veiculos"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção 3: Aparência */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2 [&>svg]:text-slate-400">
                  <Palette className="w-4 h-4" />
                  Aparência
                </h4>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>
                        Cor do Texto
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={bannerForm.corTexto}
                          onChange={(e) => setBannerForm({ ...bannerForm, corTexto: e.target.value })}
                          className="h-10 w-12 rounded-xl border border-slate-200 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={bannerForm.corTexto}
                          onChange={(e) => setBannerForm({ ...bannerForm, corTexto: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>
                        Cor do Overlay
                      </label>
                      <input
                        type="text"
                        value={bannerForm.corOverlay}
                        onChange={(e) => setBannerForm({ ...bannerForm, corOverlay: e.target.value })}
                        className={inputClass}
                        placeholder="rgba(0,0,0,0.5)"
                      />
                      <p className="text-xs text-slate-400 mt-1">Use rgba(r,g,b,a) para transparência</p>
                    </div>
                    <div>
                      <label className={labelClass}>
                        Posição do Texto
                      </label>
                      <select
                        value={bannerForm.posicaoTexto}
                        onChange={(e) => setBannerForm({ ...bannerForm, posicaoTexto: e.target.value })}
                        className={inputClass}
                      >
                        <option value="left">Esquerda</option>
                        <option value="center">Centro</option>
                        <option value="right">Direita</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção 4: Agendamento */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2 [&>svg]:text-slate-400">
                  <Clock className="w-4 h-4" />
                  Agendamento
                  <span className="text-xs text-slate-400 font-normal ml-2">
                    (opcional - deixe vazio para exibir sempre)
                  </span>
                </h4>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>
                        Exibir a partir de
                      </label>
                      <input
                        type="date"
                        value={bannerForm.dataInicio}
                        onChange={(e) => setBannerForm({ ...bannerForm, dataInicio: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        Exibir até
                      </label>
                      <input
                        type="date"
                        value={bannerForm.dataFim}
                        onChange={(e) => setBannerForm({ ...bannerForm, dataFim: e.target.value })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer com botões */}
            <div className="flex justify-end gap-3 p-4 border-t border-slate-100 bg-slate-50 sticky bottom-0">
                <Button type="button" variant="secondary" onClick={() => setShowBannerModal(false)}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveBanner}
                  disabled={savingBanner}
                >
                  {savingBanner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingBanner ? 'Salvar Alterações' : 'Criar Banner'}
                </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
